import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Icon } from "./icons";
import { formatTime } from "../lib/format";
import { CATEGORY_LABEL } from "../lib/reportStore";

export interface AudioHandle {
  playFrom: (time: number) => void;
  // Play from `from` to `to` and stop there (Find mode's clip of one hit).
  // `onDone` once it has played to its end; anything else that moves the
  // playhead first (a seek, another play) drops the clip without calling it.
  playClip: (from: number, to: number, onDone?: () => void) => void;
  toggle: () => void;
  skip: (delta: number) => void;
}

// A flag to mark on the track — `time` is its audio position (s), `category`
// drives the tick colour (reuses the report's `.m-<category>` colour vars).
export interface PlayerFlag {
  key: string;
  time: number | null;
  category: string;
}

// Find mode: where each hit is on this file's track, and the clip playing.
export interface PlayerFind {
  ticks: { key: string; time: number; now: boolean }[];
  clip: [number, number] | null;
  onTick: (key: string) => void;
}

interface Props {
  src: string;
  preroll: number;
  flags?: PlayerFlag[];
  find?: PlayerFind;
  onTime?: (t: number) => void;
  onPrevChapter?: () => void;
  onNextChapter?: () => void;
  canPrev?: boolean;
  canNext?: boolean;
}

const SPEEDS = [1, 1.25, 1.5, 0.75];

export const AudioPlayer = forwardRef<AudioHandle, Props>(function AudioPlayer(
  { src, preroll, flags = [], find, onTime, onPrevChapter, onNextChapter, canPrev, canNext },
  ref
) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [loop, setLoop] = useState(false);

  // A clip playing (Find mode): where it stops, and who to tell.
  const clipRef = useRef<{ to: number; onDone?: () => void } | null>(null);
  useEffect(() => { clipRef.current = null; }, [src]);

  // Seek to `time` (minus preroll) and reliably resume. Pause first to cancel
  // any in-flight play()/seek — otherwise seeking to a new (especially nearby)
  // spot while already playing can be dropped and the previous spot keeps going.
  function playFromTime(time: number, lead = preroll) {
    const a = audioRef.current;
    if (!a) return;
    clipRef.current = null;
    a.pause();
    a.currentTime = Math.max(0, time - lead);
    const resume = () => {
      const p = a.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    };
    a.addEventListener("seeked", resume, { once: true });
    resume();
  }

  useImperativeHandle(ref, () => ({
    playFrom: (time: number) => playFromTime(time),
    playClip(from: number, to: number, onDone?: () => void) {
      playFromTime(from, 0);
      clipRef.current = { to, onDone };
    },
    toggle() {
      const a = audioRef.current;
      if (!a) return;
      if (a.paused) a.play().catch(() => {});
      else a.pause();
    },
    skip(delta: number) {
      const a = audioRef.current;
      if (!a) return;
      a.currentTime = Math.max(0, Math.min(a.duration || 0, a.currentTime + delta));
    },
  }));

  function togglePlay() {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) a.play().catch(() => {});
    else a.pause();
  }
  function nudge(delta: number) {
    const a = audioRef.current;
    if (!a) return;
    clipRef.current = null;
    a.currentTime = Math.max(0, Math.min(a.duration || 0, a.currentTime + delta));
  }
  function seekToRatio(r: number) {
    const a = audioRef.current;
    if (!a || !a.duration) return;
    clipRef.current = null;
    a.currentTime = Math.max(0, Math.min(a.duration, r * a.duration));
  }
  function cycleSpeed() {
    const next = SPEEDS[(SPEEDS.indexOf(speed) + 1) % SPEEDS.length];
    setSpeed(next);
    if (audioRef.current) audioRef.current.playbackRate = next;
  }
  function toggleLoop() {
    const a = audioRef.current;
    if (!a) return;
    a.loop = !a.loop;
    setLoop(a.loop);
  }

  // Pointer scrubbing on the capsule track (click to seek, drag to scrub).
  function ratioFromX(clientX: number): number {
    const el = trackRef.current;
    if (!el) return 0;
    const r = el.getBoundingClientRect();
    return Math.max(0, Math.min(1, (clientX - r.left) / r.width));
  }
  function onTrackDown(e: React.PointerEvent<HTMLDivElement>) {
    draggingRef.current = true;
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* no active pointer */ }
    seekToRatio(ratioFromX(e.clientX));
  }
  function onTrackMove(e: React.PointerEvent<HTMLDivElement>) {
    if (draggingRef.current) seekToRatio(ratioFromX(e.clientX));
  }
  function onTrackUp(e: React.PointerEvent<HTMLDivElement>) {
    draggingRef.current = false;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* already released */ }
  }

  const pct = dur ? Math.max(0, Math.min(1, cur / dur)) * 100 : 0;

  return (
    <div className="transport">
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onLoadedMetadata={(e) => {
          setDur(e.currentTarget.duration);
          e.currentTarget.playbackRate = speed;
        }}
        onTimeUpdate={(e) => {
          const t = e.currentTarget.currentTime;
          setCur(t);
          onTime?.(t);
          const clip = clipRef.current;
          if (clip && t >= clip.to) {
            clipRef.current = null;
            e.currentTarget.pause();
            clip.onDone?.();
          }
        }}
      />

      <div className="tp-ctrls">
        <button className="tp-btn" disabled={!canPrev} onClick={onPrevChapter} title="Previous chapter">
          <Icon name="skip-back" size={19} />
        </button>
        <button className="tp-btn tp-jump" onClick={() => nudge(-5)} title="Back 5 seconds">
          <Icon name="rotate-ccw" size={20} />
          <span className="tp-j-n">5</span>
        </button>
        <button className="tp-play" onClick={togglePlay} title="Play / pause (Space)">
          <Icon name={playing ? "pause" : "play"} size={21} />
        </button>
        <button className="tp-btn tp-jump" onClick={() => nudge(5)} title="Forward 5 seconds">
          <Icon name="rotate-cw" size={20} />
          <span className="tp-j-n">5</span>
        </button>
        <button className="tp-btn" disabled={!canNext} onClick={onNextChapter} title="Next chapter">
          <Icon name="skip-forward" size={19} />
        </button>
      </div>

      <span className="tp-tc">{formatTime(cur)}</span>

      <div
        className="tp-track"
        ref={trackRef}
        onPointerDown={onTrackDown}
        onPointerMove={onTrackMove}
        onPointerUp={onTrackUp}
        onPointerCancel={onTrackUp}
        title="Click or drag to seek"
      >
        <div className="tp-groove">
          <div className="tp-fill" style={{ width: `${pct}%` }} />
          {dur > 0 && find?.clip && (
            <div className="tp-clip" style={{
              left: `${Math.min(1, find.clip[0] / dur) * 100}%`,
              width: `${Math.max(0.4, Math.min(1, (find.clip[1] - find.clip[0]) / dur) * 100)}%`,
            }} />
          )}
          {dur > 0 && find?.ticks.map((f) => (
            <button
              key={f.key}
              className={`tp-find${f.now ? " now" : ""}`}
              style={{ left: `${Math.max(0, Math.min(1, f.time / dur)) * 100}%` }}
              title={`Found here · ${formatTime(f.time)}`}
              aria-label={`Found here at ${formatTime(f.time)}`}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); find.onTick(f.key); }}
            />
          ))}
          {dur > 0 &&
            flags.map((f) =>
              f.time == null ? null : (
                <button
                  key={f.key}
                  className={`tp-tick m-${f.category}`}
                  style={{ left: `${Math.max(0, Math.min(1, f.time / dur)) * 100}%` }}
                  title={`${CATEGORY_LABEL[f.category as keyof typeof CATEGORY_LABEL] ?? "Flag"} · ${formatTime(f.time)} — jump here`}
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    playFromTime(f.time as number);
                  }}
                />
              )
            )}
          <div className="tp-thumb" style={{ left: `${pct}%` }} />
        </div>
      </div>

      <span className="tp-tc dim">{formatTime(dur)}</span>

      <button className="tp-speed" onClick={cycleSpeed} title="Playback speed">
        {speed}×
      </button>
      <button className={`tp-btn ${loop ? "on" : ""}`} onClick={toggleLoop} title="Loop chapter">
        <Icon name="repeat" size={19} />
      </button>
    </div>
  );
});
